import { Component, EventEmitter, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { DeactivationDialogService } from '../../../../helper/can-deactivate/deactivation-dialog.service';
import { FeedstockService } from '../../../feedstock/feedstock.service';
import { MaterialService } from '../../../feedstock/material.service';
import { Feedstock } from '../../../../models/project/feedstock/feedstock.model';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';
import { FeedstockBmpPotential } from '../../../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockCharacteristics } from '../../../../models/project/feedstock/feedstock-characteristics.model';

@Component({
  selector: 'app-edit-feedstock-dialog',
  templateUrl: './edit-feedstock-dialog.component.html',
  styleUrls: [
    './edit-feedstock-dialog.component.scss',
  ]
})
export class EditFeedstockDialogComponent extends UserpilotReloader implements OnInit, OnDestroy {
  onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger: string;

  feedstock: Feedstock;
  feedstockUpdatedNotifier = new EventEmitter();

  originalName: string;

  isEdit = false;
  unsavedChanges = false;
  materialCategoriesLoaded = false;

  basicInfoValid = true;
  bmpValid = true;
  npkValid = true;

  constructor(
    private dialogRef: MatDialogRef<EditFeedstockDialogComponent>,
    private dialogService: DeactivationDialogService,
    private feedstockService: FeedstockService,
    private materialService: MaterialService,
    public translationService: AnessaTranslationService,
    @Inject(MAT_DIALOG_DATA) private data: { isEdit: boolean, feedstock: Feedstock },
  ) {
    super();
    this.isEdit = this.data.isEdit;
    if (this.data.isEdit) {
      this.feedstock = this.data.feedstock;
      this.originalName = this.data.feedstock.name;
    } else {
      this.feedstock = new Feedstock();
      this.feedstock.characteristics.densityEnabled = this.feedstockService.getDensityToggle();
      this.feedstock.characteristics.characteristicsEnabled = this.feedstockService.getCharacteristicsToggle();
      this.feedstock.npkEnabled = this.feedstockService.getNPKToggle();
      this.feedstock.characteristics.codEnabled = this.feedstockService.getCODToggle();
      // Set default assumptions
      this.feedstock.financial.feedstockCpi.value = 0;
      this.feedstock.financial.feedstockCpi.unit = '000000000000000000002201';
      this.feedstock.financial.transportCpi.value = 0;
      this.feedstock.financial.transportCpi.unit = '000000000000000000002201';
    }
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });

    this.materialService.feedstockMaterialsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe(loaded => {
        this.materialCategoriesLoaded = loaded;
      });

    this.dialogRef.backdropClick().subscribe(() => {
      if (this.unsavedChanges) {
        this.dialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'))
          .pipe(take(1)).subscribe((result: boolean) => {
            if (result) {
              this.dialogRef.close();
            }
          });
      } else {
        this.dialogRef.close();
      }
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  onChangesMade() {
    this.unsavedChanges = true;
  }

  onSaveClick() {
    if (!this.feedstock.characteristics.characteristicsEnabled) {
      let densityEnabled = this.feedstock.characteristics.densityEnabled;
      this.feedstock.characteristics = new FeedstockCharacteristics();
      this.feedstock.characteristics.densityEnabled = densityEnabled;
      this.feedstock.bmpPotential = new FeedstockBmpPotential();
    }
    this.dialogRef.close(this.feedstock);
  }

  onCancelClick() {
    this.dialogRef.close(null);
  }

  getPageHeading() {
    if (this.isEdit && this.feedstock) {
      const name = this.originalName ? this.originalName : '';
      return `${this.translationService.get('PROJECT.FEEDSTOCK.TITLE_EDIT')}: ${name}`;
    } else {
      return this.translationService.get('PROJECT.FEEDSTOCK.TITLE_ADD');
    }
  }

  feedstockHasName(): boolean {
    return !!(this.feedstock && this.feedstock.name);
  }
}
